import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  englishStart,
  fullContent,
  baseText,
  MessageText,
  straightenQuotes,
} from './copyModel';

import {
  CopyDetail,
  CopyLocale,
  lifecycleAPI,
  isConflict,
  isNotFound,
  lifecycleErrorMessage,
} from '@/services/lifecycleAPI';
import {
  LifecycleAction,
  lifecycleFailureOutcome,
  LifecycleSurface,
  trackLifecycleActionTaken,
} from '@/util/vexo';

const fieldKey = (variant: string, field: string) => `${variant}/${field}`;

// The edits made after a request took `sent`: the fields stay editable while
// it is in flight, and clearing every edit on its reply lost the words typed
// meanwhile (review R12-10). A field still as it was sent is saved and drops.
const editsSince = (sent: MessageText, now: MessageText): MessageText => {
  const left: MessageText = {};
  Object.entries(now).forEach(([variant, fields]) => {
    Object.entries(fields).forEach(([field, value]) => {
      if (sent[variant]?.[field] === value) return;
      left[variant] = { ...left[variant], [field]: value };
    });
  });
  return left;
};

// Two different 409s: someone published since the admin loaded (reload), or two
// saves of the draft collided (try again). Only the first gets the banner.
const DRAFT_RACE = /saved a draft/i;
// The draft was saved against an older live text (a revert or reset since):
// the server message says to review the draft, so it is shown as it is.
const LIVE_CHANGED = /live text changed/i;
const isStale = (err: unknown) => {
  if (!isConflict(err)) return false;
  const message = lifecycleErrorMessage(err, '');
  return !DRAFT_RACE.test(message) && !LIVE_CHANGED.test(message);
};
// Someone else saved or discarded the draft since this admin opened it.
const DRAFT_CHANGED = /changed the draft/i;

// The draft this admin last saw, sent with every write that takes it
// (null: there was none). graphy refuses the write when it has changed.
const draftTokenOf = (detail: CopyDetail | null) =>
  detail?.draft?.token ?? null;

// The draft was saved on an older live version (a revert or reset since).
// graphy refuses to publish it until it is saved again on the live version.
const draftIsStale = (detail: CopyDetail | null) => {
  const based = detail?.draft?.base_version;
  return based != null && based !== detail?.version;
};

// `surface` is the screen editing the message: the step window or the full
// editor. It only labels the analytics event.
const useCopyMessage = (
  stepKey: string | null,
  locale: CopyLocale,
  surface: LifecycleSurface = 'copy_editor',
) => {
  const [detail, setDetail] = useState<CopyDetail | null>(null);
  const [loading, setLoading] = useState(Boolean(stepKey));
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [work, setWork] = useState<MessageText>({});
  // The edits as they were when a save or publish started (`apply`).
  const workRef = useRef<MessageText>(work);
  workRef.current = work;
  const [curly, setCurly] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [conflict, setConflict] = useState(false);
  const [draftConflict, setDraftConflict] = useState(false);
  // The published version the admin's edits are based on: the one they loaded,
  // or the one their own publish, revert, reset or discard returned. A save
  // keeps it, so a publish after a save still gets the 409 when someone else
  // published in between (the save response carries the newer version).
  const baseVersion = useRef<number | null>(null);
  const current = useRef({ stepKey, locale });
  current.current = { stepKey, locale };
  const isCurrent = useCallback(
    () =>
      current.current.stepKey === stepKey && current.current.locale === locale,
    [stepKey, locale],
  );

  useEffect(() => {
    setDetail(null);
    setLoading(Boolean(stepKey));
    setWork({});
    setCurly([]);
    setSaveError(null);
    setConflict(false);
    setDraftConflict(false);
    setError(null);
    setNotFound(false);
    baseVersion.current = null;
    if (!stepKey) {
      setLoading(false);
      return undefined;
    }
    let active = true;
    setLoading(true);
    lifecycleAPI
      .getCopy(stepKey, { locale })
      .then((result) => {
        if (!active) return;
        baseVersion.current = result.version;
        setDetail(result);
      })
      .catch((err) => {
        if (!active) return;
        if (isNotFound(err)) setNotFound(true);
        else
          setError(lifecycleErrorMessage(err, 'Could not load this message.'));
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [stepKey, locale, attempt]);

  const base = useMemo(() => (detail ? baseText(detail) : {}), [detail]);

  const text = useMemo<MessageText>(() => {
    const merged: MessageText = {};
    Object.entries(base).forEach(([variant, fields]) => {
      merged[variant] = { ...fields, ...work[variant] };
    });
    return merged;
  }, [base, work]);

  const dirty = useMemo(
    () =>
      Object.entries(work).some(([variant, fields]) =>
        Object.entries(fields).some(
          ([field, value]) => value !== base[variant]?.[field],
        ),
      ),
    [base, work],
  );

  const edit = useCallback((variant: string, field: string, raw: string) => {
    const value = straightenQuotes(raw);
    setWork((prev) => ({
      ...prev,
      [variant]: { ...prev[variant], [field]: value },
    }));
    if (value !== raw) {
      setCurly((prev) => {
        const key = fieldKey(variant, field);
        return prev.includes(key) ? prev : [...prev, key];
      });
    }
    setSaveError(null);
  }, []);

  const apply = useCallback(
    async (
      action: LifecycleAction,
      run: () => Promise<CopyDetail>,
      fallback: string,
      { keepBase = false, keepEdits = false } = {},
    ): Promise<CopyDetail | null> => {
      setSaving(true);
      setSaveError(null);
      // keepEdits: the request sends no text (a publish of the draft just
      // saved), so every edit still here is newer than what it publishes.
      const sent = keepEdits ? {} : workRef.current;
      const track = (outcome: 'success' | 'conflict' | 'error') =>
        trackLifecycleActionTaken({
          action,
          outcome,
          surface,
          journey_key: detail?.journey_key ?? null,
          step_key: stepKey,
          locale,
        });
      try {
        const result = await run();
        track('success');
        if (!isCurrent()) return null;
        if (!keepBase) baseVersion.current = result.version;
        setDetail(result);
        setWork((now) => editsSince(sent, now));
        setCurly([]);
        return result;
      } catch (err) {
        track(lifecycleFailureOutcome(err));
        if (isCurrent()) {
          if (isStale(err)) {
            setConflict(true);
            setDraftConflict(
              DRAFT_CHANGED.test(lifecycleErrorMessage(err, '')),
            );
          } else setSaveError(lifecycleErrorMessage(err, fallback));
        }
        return null;
      } finally {
        setSaving(false);
      }
    },
    [isCurrent, detail, stepKey, locale, surface],
  );

  const loadedVersion = useCallback(
    (fallback: CopyDetail) => baseVersion.current ?? fallback.version,
    [],
  );

  const save = useCallback(async (): Promise<CopyDetail | null> => {
    if (!detail || !stepKey) return null;
    return apply(
      'copy_save_draft',
      () =>
        lifecycleAPI.saveCopyDraft(stepKey, {
          content: fullContent(text, locale),
          version: loadedVersion(detail),
          locale,
          draftToken: draftTokenOf(detail),
        }),
      'Could not save the draft.',
      { keepBase: true },
    );
  }, [apply, detail, loadedVersion, stepKey, text, locale]);

  const publish = useCallback(
    async (onDraftSaved?: () => void): Promise<CopyDetail | null> => {
      if (!detail || !stepKey) return null;
      // Captured before the save: the save swaps `detail` for the server's
      // latest, and publishing with that would skip the conflict check.
      const version = loadedVersion(detail);
      // The draft this publish is for: the one just saved, or the one shown.
      let draftToken = draftTokenOf(detail);
      if (dirty || draftIsStale(detail)) {
        const saved = await save();
        if (!saved) return null;
        draftToken = draftTokenOf(saved);
        onDraftSaved?.();
        const unresolved =
          (saved.draft?.structure_errors.length ?? 0) > 0 ||
          Object.values(saved.draft?.lint_errors ?? {}).some(
            (list) => list.length > 0,
          );
        if (unresolved) return null;
      }
      return apply(
        'copy_publish',
        () =>
          lifecycleAPI.publishCopy(stepKey, { version, locale, draftToken }),
        'Could not publish the message.',
        { keepEdits: true },
      );
    },
    [apply, detail, dirty, loadedVersion, save, stepKey, locale],
  );

  const discard = useCallback(async (): Promise<CopyDetail | null> => {
    if (!stepKey) return null;
    return apply(
      'copy_discard',
      () =>
        lifecycleAPI.discardCopyDraft(stepKey, {
          locale,
          draftToken: draftTokenOf(detail),
        }),
      'Could not discard the draft.',
    );
  }, [apply, detail, stepKey, locale]);

  // A reset or revert publishes but leaves a saved draft in place, and the
  // editor shows the draft. Drop it so the fields show what is live. Only the
  // draft this admin saw: one another admin saved since is kept (graphy
  // refuses the discard).
  const publishAndDropDraft = useCallback(
    async (
      action: LifecycleAction,
      run: () => Promise<CopyDetail>,
      fallback: string,
      live: string,
    ): Promise<CopyDetail | null> => {
      if (!detail || !stepKey) return null;
      let draftKept: 'failed' | 'other' | null = null;
      const result = await apply(
        action,
        async () => {
          const done = await run();
          if (!done.draft) return done;
          return lifecycleAPI
            .discardCopyDraft(stepKey, {
              locale,
              draftToken: draftTokenOf(detail),
            })
            .catch((err: unknown) => {
              draftKept =
                isConflict(err) &&
                DRAFT_CHANGED.test(lifecycleErrorMessage(err, ''))
                  ? 'other'
                  : 'failed';
              return done;
            });
        },
        fallback,
      );
      if (result && draftKept && isCurrent()) {
        setSaveError(
          draftKept === 'other'
            ? `${live}. Someone else saved a draft of this message since you opened it, and it was kept.`
            : `${live}, but the saved draft could not be discarded. Discard the draft, then try again.`,
        );
      }
      return result;
    },
    [apply, detail, stepKey, locale, isCurrent],
  );

  const reset = useCallback(async (): Promise<CopyDetail | null> => {
    if (!detail || !stepKey) return null;
    return publishAndDropDraft(
      'copy_reset',
      () =>
        lifecycleAPI.resetCopy(stepKey, {
          version: loadedVersion(detail),
          locale,
        }),
      'Could not reset the message.',
      'The default text is live',
    );
  }, [publishAndDropDraft, detail, loadedVersion, stepKey, locale]);

  const revert = useCallback(
    async (toVersion: number): Promise<CopyDetail | null> => {
      if (!detail || !stepKey) return null;
      return publishAndDropDraft(
        'copy_revert',
        () =>
          lifecycleAPI.revertCopy(stepKey, {
            to_version: toVersion,
            version: loadedVersion(detail),
            locale,
          }),
        'Could not revert the message.',
        `Reverted to version ${toVersion}`,
      );
    },
    [publishAndDropDraft, detail, loadedVersion, stepKey, locale],
  );

  const startSpanish = useCallback(async (): Promise<CopyDetail | null> => {
    if (!detail || !stepKey) return null;
    return apply(
      'copy_start_spanish',
      () =>
        lifecycleAPI.saveCopyDraft(stepKey, {
          content: fullContent(englishStart(detail), 'es'),
          version: loadedVersion(detail),
          locale,
          draftToken: draftTokenOf(detail),
        }),
      'Could not start the Spanish draft.',
      { keepBase: true },
    );
  }, [apply, detail, loadedVersion, stepKey, locale]);

  const retry = useCallback(() => setAttempt((n) => n + 1), []);

  const curlyFixed = useCallback(
    (variant: string, field: string) =>
      curly.includes(fieldKey(variant, field)),
    [curly],
  );

  return {
    detail,
    loading: loading || (Boolean(stepKey) && !detail && !error && !notFound),
    error,
    notFound,
    text,
    dirty,
    staleDraft: draftIsStale(detail),
    saving,
    saveError,
    edit,
    save,
    publish,
    discard,
    reset,
    revert,
    startSpanish,
    conflict,
    draftConflict,
    retry,
    curlyFixed,
    setDetail,
  };
};

export default useCopyMessage;
